'use client';

import { useEffect, useState } from 'react';
import type { Universe } from '@/types/universe';
import { GTranslateIsland } from '@/components/GTranslateIsland';

interface NavbarProps {
  navScrolled: boolean;
  universes: Universe[];
  activeUniverse: number;
  onUniverseChange: (idx: number) => void;
}

const navItems = ['Guide', 'Comment ça marche', 'Avis', 'Production', 'Tarifs'];
const navHrefs = ['#deliverables', '#how-it-works', '#testimonials', '#videos', '#pricing'];

function Chevron({ open, size = 12 }: { open: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export default function Navbar({ navScrolled, universes, activeUniverse, onUniverseChange }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [univOpen, setUnivOpen] = useState(false);
  const [mobileUnivOpen, setMobileUnivOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const close = () => {
    setMenuOpen(false);
    setMobileUnivOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 px-5 md:px-10 py-2 flex items-center justify-between gap-3 transition-all duration-500 ${
          navScrolled ? 'nav-scrolled' : 'bg-transparent'
        }`}>
        {/* Logo */}
        <a href="#" className="flex items-center shrink-0">
          <img src="/assets/images/logo.png" alt="DreamerLife" className="h-16 md:h-16 lg:h-24 w-auto" />
        </a>

        {/* Nav desktop — lg+ */}
        <div className="hidden lg:flex items-center gap-1 bg-white/10 backdrop-blur-md rounded-full border border-white/10 px-1 py-1">
          {/* Univers (menu déroulant) */}
          <div
            className="relative"
            onMouseEnter={() => setUnivOpen(true)}
            onMouseLeave={() => setUnivOpen(false)}>
            <button
              type="button"
              onClick={() => setUnivOpen((o) => !o)}
              aria-expanded={univOpen}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/15 transition-colors duration-200">
              Thématiques
              <Chevron open={univOpen} />
            </button>
            {univOpen &&
              <div className="absolute top-full left-0 mt-2 min-w-[230px] rounded-2xl border border-white/10 bg-primary/95 backdrop-blur-md p-2 shadow-xl">
                {universes.map((u, i) =>
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { onUniverseChange(i); setUnivOpen(false); }}
                    className={`w-full px-3 py-2 rounded-xl text-left text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                      i === activeUniverse ? 'bg-accent text-primary' : 'text-white hover:bg-white/10'
                    }`}>
                    {u.label}
                  </button>
                )}
              </div>
            }
          </div>

          {/* Autres liens */}
          {navItems.map((item, i) =>
            <a
              key={item}
              href={navHrefs[i]}
              className="px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/15 transition-colors duration-200">
              {item}
            </a>
          )}
        </div>

        {/* Cluster droite */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Langues : UN SEUL widget, jamais en display:none. */}
          <div
            className={`flex transition-opacity duration-200
              absolute right-4 top-full mt-2 rounded-2xl border border-white/10 bg-primary/95 p-3 shadow-xl backdrop-blur-md
              ${langOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'}
              md:static md:right-auto md:top-auto md:mt-0 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:backdrop-blur-none
              md:opacity-100 md:visible md:pointer-events-auto`}>
            <GTranslateIsland />
          </div>

          {/* CTA */}
          <a
            href="#pricing"
            className="hidden sm:inline-flex items-center bg-accent text-primary font-semibold text-[11px] uppercase tracking-widest px-5 py-3 rounded-full hover:bg-accent-light transition-colors duration-200">
            Commencer
          </a>

          {/* Bouton langue (mobile) */}
          <button
            type="button"
            onClick={() => { setLangOpen((o) => !o); setMenuOpen(false); }}
            aria-label="Choisir la langue"
            aria-expanded={langOpen}
            className="md:hidden p-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/10 text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" />
            </svg>
          </button>

          {/* Burger menu — sous lg */}
          <button
            type="button"
            onClick={() => { setMenuOpen((o) => !o); setLangOpen(false); }}
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
            className="lg:hidden p-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/10 text-white">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {menuOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M3 12h18M3 6h18M3 18h18" />}
            </svg>
          </button>
        </div>
      </nav>

      {/* Menu mobile plein écran (< lg) */}
      {menuOpen &&
        <div className="fixed inset-0 z-40 lg:hidden overflow-y-auto bg-primary/95 backdrop-blur-md">
          <div className="min-h-full flex flex-col items-center justify-center gap-7 [@media(max-height:600px)]:gap-4 px-6 py-24 [@media(max-height:600px)]:py-20">
            {/* Univers (dépliable) */}
            <div className="flex flex-col items-center gap-4 [@media(max-height:600px)]:gap-2">
              <button
                type="button"
                onClick={() => setMobileUnivOpen((o) => !o)}
                aria-expanded={mobileUnivOpen}
                className="flex items-center gap-2 text-2xl [@media(max-height:600px)]:text-xl text-white hover:text-accent transition-colors duration-200"
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                Thématiques
                <Chevron open={mobileUnivOpen} size={18} />
              </button>
              {mobileUnivOpen &&
                <div className="flex flex-col items-center gap-3 [@media(max-height:600px)]:gap-1.5">
                  {universes.map((u, i) =>
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { onUniverseChange(i); close(); }}
                      className={`text-lg [@media(max-height:600px)]:text-base transition-colors duration-200 ${
                        i === activeUniverse ? 'text-accent' : 'text-white/80 hover:text-white'
                      }`}>
                      {u.label}
                    </button>
                  )}
                </div>
              }
            </div>

            {/* Autres liens */}
            {navItems.map((item, i) =>
              <a
                key={item}
                href={navHrefs[i]}
                onClick={close}
                className="text-2xl [@media(max-height:600px)]:text-xl text-white hover:text-accent transition-colors duration-200"
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                {item}
              </a>
            )}

            <a
              href="#pricing"
              onClick={close}
              className="mt-2 [@media(max-height:600px)]:mt-1 bg-accent text-primary font-semibold text-xs uppercase tracking-widest px-8 py-3 rounded-full">
              Commencer
            </a>
          </div>
        </div>
      }
    </>
  );
}